/**
 * MerakiDebrandingPage — backup and restore network configurations.
 *
 * Flow:
 *   1. Pick a network → see existing backups from the database
 *   2. "Run backup" → progress dialog → new backup appears in the list
 *   3. "Restore →" on any backup row → target-selection dialog
 *      ("Same network" button or NetworkPicker for another network)
 *      → progress dialog → success / failure confirmation
 */
import { useEffect, useMemo, useReducer, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowDownToLine,
  CheckCircle2,
  Database,
  ExternalLink,
  Info,
  RotateCcw,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { Stepper } from "@/components/ui/stepper";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  TableShell,
  TableHeadRow,
  TableRow,
  TableHeadCell,
  TableCell,
} from "@/components/ui/table-shell";
import { NetworkPicker } from "@/components/app/network-picker";
import { MerakiSubNav } from "@/components/app/meraki-sub-nav";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import { useMerakiDebranding } from "@/stores/meraki-debranding";
import { useMerakiKeys } from "@/stores/meraki-keys";
import type { MerakiNetwork, NetworkBackup } from "@/api/schemas/meraki";
import { ApiError, downloadBlob } from "@/api/client";

const COLS = "0.6fr 1.4fr 0.9fr 1fr 0.7fr";

/* ── Sub-components extracted to keep MerakiDebrandingPage under 300 lines ── */

function BackupProgressDialog({
  backupOpen,
  backupDone,
  backupMessage,
  backupPct,
  backups,
  setBackup,
}: {
  backupOpen: boolean;
  backupDone: boolean;
  backupMessage: string;
  backupPct: number | undefined;
  backups: NetworkBackup[];
  setBackup: (p: { backupOpen?: boolean }) => void;
}) {
  return (
    <Dialog open={backupOpen} onOpenChange={(v) => setBackup({ backupOpen: v })}>
      <DialogContent>
        {backupDone ? (
          <div className="flex flex-col items-start gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-mint/15 text-mint">
              <CheckCircle2 size={22} />
            </div>
            <DialogTitle>Backup complete</DialogTitle>
            <DialogDescription>
              Snapshot saved. Download it now or find it any time in the history below.
            </DialogDescription>
            <div className="flex items-center gap-3 pt-1">
              {backups[0] ? (
                <Button
                  onClick={() => {
                    void onDownload(backups[0]!);
                    setBackup({ backupOpen: false });
                  }}
                >
                  <ArrowDownToLine size={14} />
                  Download backup
                </Button>
              ) : null}
              <Button variant="ghost" onClick={() => setBackup({ backupOpen: false })}>
                Close
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <DialogTitle>Backing up network</DialogTitle>
            <DialogDescription>{backupMessage}</DialogDescription>
            <Progress value={backupPct} hint={backupPct ? undefined : "Establishing connection…"} />
            <div className="text-[11px] text-fg-mute">
              Closing this dialog won't cancel the backup — it will continue in the background.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RestoreSelectionDialog({
  restoreTarget,
  restoreNetwork,
  restoreUseSame,
  resolvedRestoreNetworkId,
  setRestoreSel,
  onConfirmRestore,
}: {
  restoreTarget: NetworkBackup | null;
  restoreNetwork: MerakiNetwork | null;
  restoreUseSame: boolean;
  resolvedRestoreNetworkId: string | null;
  setRestoreSel: (p: { restoreTarget?: NetworkBackup | null; restoreNetwork?: MerakiNetwork | null; restoreUseSame?: boolean }) => void;
  onConfirmRestore: () => void;
}) {
  return (
    <Dialog
      open={restoreTarget !== null}
      onOpenChange={(open) => { if (!open) setRestoreSel({ restoreTarget: null }); }}
    >
      <DialogContent className="max-w-lg">
        <div className="flex flex-col gap-5">
          <div>
            <DialogTitle>Restore snapshot</DialogTitle>
            <DialogDescription className="mt-1">
              Choose which network this snapshot should be applied to.
            </DialogDescription>
          </div>

          {restoreTarget ? (
            <div className="rounded-xl border border-line bg-surface-raised px-4 py-3 text-sm">
              <div className="font-mono text-xs text-fg-mute">{restoreTarget.id}</div>
              <div className="mt-1 font-semibold text-fg">{restoreTarget.networkName}</div>
              <div className="mt-0.5 text-xs text-fg-mute">
                {new Date(restoreTarget.createdAt).toUTCString()}
              </div>
              {restoreTarget.summary ? (
                <div className="mt-2 flex flex-wrap gap-1">
                  {restoreTarget.summary.ssidCount !== undefined ? (
                    <Badge size="sm" variant="neutral">{restoreTarget.summary.ssidCount} SSIDs</Badge>
                  ) : null}
                  {restoreTarget.summary.vlanCount !== undefined ? (
                    <Badge size="sm" variant="neutral">{restoreTarget.summary.vlanCount} VLANs</Badge>
                  ) : null}
                  {restoreTarget.summary.portCount !== undefined ? (
                    <Badge size="sm" variant="neutral">{restoreTarget.summary.portCount} ports</Badge>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-col gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-fg-mute">
              Restore destination
            </p>
            <button
              type="button"
              onClick={() => setRestoreSel({ restoreUseSame: true, restoreNetwork: null })}
              className={[
                "flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors",
                restoreUseSame
                  ? "border-violet bg-aurora-soft text-fg"
                  : "border-line bg-surface hover:border-violet/50 text-fg-dim hover:text-fg",
              ].join(" ")}
            >
              <div className={[
                "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg",
                restoreUseSame ? "bg-violet/20 text-violet" : "bg-surface-raised text-fg-mute",
              ].join(" ")}>
                <RotateCcw size={15} />
              </div>
              <div>
                <div className="text-sm font-semibold">Same network</div>
                <div className="mt-0.5 font-mono text-[11px] text-fg-mute">
                  {restoreTarget?.networkName}
                </div>
              </div>
            </button>

            <div
              className={[
                "flex flex-col gap-3 rounded-xl border px-4 py-3 transition-colors",
                !restoreUseSame && restoreNetwork
                  ? "border-violet bg-aurora-soft"
                  : !restoreUseSame
                    ? "border-violet/40 bg-surface"
                    : "border-line bg-surface",
              ].join(" ")}
            >
              <button
                type="button"
                onClick={() => setRestoreSel({ restoreUseSame: false })}
                className="flex w-full items-center gap-3 text-left"
              >
                <div className={[
                  "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg",
                  !restoreUseSame ? "bg-violet/20 text-violet" : "bg-surface-raised text-fg-mute",
                ].join(" ")}>
                  <Database size={15} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-fg">Other network</div>
                  <div className="mt-0.5 text-[11px] text-fg-mute">Choose a different target network</div>
                </div>
              </button>
              {!restoreUseSame ? (
                <NetworkPicker
                  value={restoreNetwork}
                  onChange={(n) => setRestoreSel({ restoreNetwork: n })}
                  placeholder="Select target network…"
                />
              ) : null}
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-line pt-4">
            <Button variant="ghost" onClick={() => setRestoreSel({ restoreTarget: null })}>
              Cancel
            </Button>
            <Button disabled={!resolvedRestoreNetworkId} onClick={onConfirmRestore}>
              <RotateCcw size={13} />
              Restore snapshot
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RestoreProgressDialog({
  restoreProgressOpen,
  restoreDone,
  restoreSuccess,
  restoreFinalMessage,
  restoreMessage,
  restorePct,
  setRestoreProgress,
}: {
  restoreProgressOpen: boolean;
  restoreDone: boolean;
  restoreSuccess: boolean;
  restoreFinalMessage: string;
  restoreMessage: string;
  restorePct: number | undefined;
  setRestoreProgress: (p: { restoreProgressOpen?: boolean }) => void;
}) {
  return (
    <Dialog
      open={restoreProgressOpen}
      onOpenChange={(open) => { if (!open && restoreDone) setRestoreProgress({ restoreProgressOpen: false }); }}
    >
      <DialogContent>
        {restoreDone ? (
          <div className="flex flex-col items-start gap-4">
            <div className={[
              "flex h-12 w-12 items-center justify-center rounded-2xl",
              restoreSuccess ? "bg-mint/15 text-mint" : "bg-peach/15 text-peach",
            ].join(" ")}>
              {restoreSuccess ? <CheckCircle2 size={22} /> : <XCircle size={22} />}
            </div>
            <DialogTitle>
              {restoreSuccess ? "Restore complete" : "Restore failed"}
            </DialogTitle>
            <DialogDescription>{restoreFinalMessage}</DialogDescription>
            <Button
              variant={restoreSuccess ? "primary" : "ghost"}
              onClick={() => setRestoreProgress({ restoreProgressOpen: false })}
            >
              Close
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <DialogTitle>Restoring snapshot…</DialogTitle>
            <DialogDescription>{restoreMessage}</DialogDescription>
            <Progress value={restorePct} hint={restorePct ? undefined : "Connecting…"} />
            <div className="text-[11px] text-fg-mute">
              This may take a moment. The dialog will update automatically when done.
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BackupHistorySection({
  network,
  backups,
  status,
  loadFor,
  onClickRestore,
}: {
  network: MerakiNetwork;
  backups: NetworkBackup[];
  status: string;
  loadFor: (id: string) => void;
  onClickRestore: (b: NetworkBackup) => void;
}) {
  return (
    <Card>
      <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-4">
        <div>
          <CardTitle>Backup history</CardTitle>
          <CardSub className="mt-1">
            {backups.length} {backups.length === 1 ? "snapshot" : "snapshots"} for{" "}
            <b className="font-mono text-fg">{network.name}</b>
          </CardSub>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void loadFor(network.id)}
          disabled={status === "loading"}
        >
          {status === "loading" ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      {backups.length === 0 ? (
        <div className="flex flex-col items-center gap-3 px-5 py-12 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-aurora-soft text-violet">
            <Database size={20} />
          </div>
          <div>
            <CardTitle>No snapshots yet</CardTitle>
            <CardSub className="mt-1">
              Run a backup above to capture the current configuration.
            </CardSub>
          </div>
        </div>
      ) : (
        <TableShell className="rounded-none border-0 border-t border-line">
          <TableHeadRow columns={COLS}>
            <TableHeadCell>Status</TableHeadCell>
            <TableHeadCell>Snapshot</TableHeadCell>
            <TableHeadCell>Created by</TableHeadCell>
            <TableHeadCell>Restored into</TableHeadCell>
            <TableHeadCell />
          </TableHeadRow>
          {backups.map((b) => {
            const restored = !!b.restoredAt;
            return (
              <TableRow key={b.id} columns={COLS}>
                <TableCell>
                  {restored ? (
                    <Badge variant="info" dot>restored</Badge>
                  ) : (
                    <Badge variant="ok">
                      <ArrowDownToLine size={9} />
                      ready
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="font-mono text-xs text-fg">{b.id}</div>
                  <div className="mt-0.5 text-[10.5px] text-fg-mute">
                    {new Date(b.createdAt).toUTCString()}
                  </div>
                  {b.summary ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {b.summary.ssidCount !== undefined ? (
                        <Badge size="sm" variant="neutral">
                          {b.summary.ssidCount} SSID{b.summary.ssidCount === 1 ? "" : "s"}
                        </Badge>
                      ) : null}
                      {b.summary.portCount !== undefined ? (
                        <Badge size="sm" variant="neutral">{b.summary.portCount} ports</Badge>
                      ) : null}
                      {b.summary.vlanCount !== undefined ? (
                        <Badge size="sm" variant="neutral">{b.summary.vlanCount} VLANs</Badge>
                      ) : null}
                    </div>
                  ) : null}
                </TableCell>
                <TableCell className="text-xs text-fg">{b.createdBy}</TableCell>
                <TableCell>
                  {restored && b.restoredIntoNetworkId ? (
                    <div>
                      <div className="font-mono text-[11px] text-fg">{b.restoredIntoNetworkId}</div>
                      <div className="text-[10.5px] text-fg-mute">
                        by {b.restoredBy} ·{" "}
                        {b.restoredAt ? new Date(b.restoredAt).toLocaleDateString() : ""}
                      </div>
                    </div>
                  ) : (
                    <span className="text-xs text-fg-mute">—</span>
                  )}
                </TableCell>
                <TableCell className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => void onDownload(b)}>
                    Download
                  </Button>
                  <Button size="sm" onClick={() => onClickRestore(b)}>
                    <RotateCcw size={12} />
                    Restore
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableShell>
      )}
    </Card>
  );
}

async function onDownload(backup: NetworkBackup) {
  try {
    await downloadBlob(
      `/meraki/debranding/network-backups/${backup.id}/download/`,
      `meraki-backup-${backup.id}.json`,
    );
  } catch (err) {
    const msg = err instanceof ApiError ? `API error ${err.status}` : "Download failed";
    toast.error("Could not download backup", { description: msg });
  }
}

export function MerakiDebrandingPage() {
  const {
    backupsByNetwork,
    error,
    status,
    loadFor,
    createBackup,
    restoreBackup,
    pollTask,
  } = useMerakiDebranding();
  const { data: keys, load: loadKeys } = useMerakiKeys();

  const [network, setNetwork] = useState<MerakiNetwork | null>(null);

  // ── backup progress dialog ────────────────────────────────────────────────
  type BackupState = { backupOpen: boolean; backupPct: number | undefined; backupMessage: string; backupDone: boolean };
  const [{ backupOpen, backupPct, backupMessage, backupDone }, setBackup] = useReducer(
    (s: BackupState, p: Partial<BackupState>) => ({ ...s, ...p }),
    { backupOpen: false, backupPct: undefined, backupMessage: "Connecting to Meraki…", backupDone: false },
  );

  // ── restore target-selection dialog ──────────────────────────────────────
  type RestoreSelState = { restoreTarget: NetworkBackup | null; restoreNetwork: MerakiNetwork | null; restoreUseSame: boolean };
  const [{ restoreTarget, restoreNetwork, restoreUseSame }, setRestoreSel] = useReducer(
    (s: RestoreSelState, p: Partial<RestoreSelState>) => ({ ...s, ...p }),
    { restoreTarget: null, restoreNetwork: null, restoreUseSame: false },
  );

  // ── restore progress dialog ───────────────────────────────────────────────
  type RestoreProgressState = { restoreProgressOpen: boolean; restorePct: number | undefined; restoreMessage: string; restoreDone: boolean; restoreSuccess: boolean; restoreFinalMessage: string };
  const [{ restoreProgressOpen, restorePct, restoreMessage, restoreDone, restoreSuccess, restoreFinalMessage }, setRestoreProgress] = useReducer(
    (s: RestoreProgressState, p: Partial<RestoreProgressState>) => ({ ...s, ...p }),
    { restoreProgressOpen: false, restorePct: undefined, restoreMessage: "Preparing restore…", restoreDone: false, restoreSuccess: false, restoreFinalMessage: "" },
  );

  // Resolve the active Meraki key
  useEffect(() => { void loadKeys(); }, [loadKeys]);
  const activeKeyId = useMemo(() => {
    const active = keys.find((k) => k.active);
    return (active ?? keys[0])?.id ?? null;
  }, [keys]);


  const backups: NetworkBackup[] = network
    ? (backupsByNetwork[network.id] ?? [])
    : [];

  // ── restore target: resolved network id ──────────────────────────────────
  const resolvedRestoreNetworkId = useMemo(() => {
    if (!restoreTarget) return null;
    if (restoreUseSame) return restoreTarget.networkId;
    return restoreNetwork?.id ?? null;
  }, [restoreTarget, restoreUseSame, restoreNetwork]);

  // ── handlers ──────────────────────────────────────────────────────────────

  async function onRunBackup() {
    if (!network) { toast.info("Pick a network first"); return; }
    if (activeKeyId === null) {
      toast.error("No Meraki key", { description: "Add or activate a key first." });
      return;
    }
    setBackup({ backupOpen: true, backupDone: false, backupPct: undefined, backupMessage: "Queueing backup task…" });

    try {
      const taskId = await createBackup(network.id, String(activeKeyId));
      if (!taskId) {
        toast.success("Backup queued", {
          description: "It will appear in the history once it finishes.",
        });
        setBackup({ backupOpen: false });
        await loadFor(network.id);
        return;
      }
      setBackup({ backupMessage: "Reading network configuration…" });
      const final = await pollTask(taskId, (p) => {
        if (p.total && p.current !== undefined) setBackup({ backupPct: (p.current / p.total) * 100 });
        if (p.message) setBackup({ backupMessage: p.message });
      });
      if (final.state === "SUCCESS") {
        await loadFor(network.id); // refresh list first so backups[0] is the new entry
        setBackup({ backupDone: true });       // then reveal success state with download button
      } else {
        toast.error("Backup failed", { description: final.message ?? "Check the audit log." });
        setBackup({ backupOpen: false });
      }
    } catch (err) {
      const msg = err instanceof ApiError
        ? `API error ${err.status}`
        : err instanceof Error ? err.message : "unknown error";
      toast.error("Unable to run backup", { description: msg });
      setBackup({ backupOpen: false });
    }
  }

  function onClickRestore(backup: NetworkBackup) {
    setRestoreSel({ restoreTarget: backup, restoreNetwork: null, restoreUseSame: false });
  }

  async function onConfirmRestore() {
    if (!restoreTarget || !resolvedRestoreNetworkId) return;
    const backupId = restoreTarget.id;
    setRestoreSel({ restoreTarget: null });        // close selection dialog
    setRestoreProgress({ restoreProgressOpen: true, restoreDone: false, restoreSuccess: false, restorePct: undefined, restoreMessage: "Preparing restore…" });

    try {
      const taskId = await restoreBackup(backupId, resolvedRestoreNetworkId);
      if (!taskId) {
        toast.info("Restore queued — check back shortly.");
        setRestoreProgress({ restoreProgressOpen: false });
        if (network) await loadFor(network.id);
        return;
      }
      setRestoreProgress({ restoreMessage: "Loading backup snapshot…" });
      const final = await pollTask(taskId, (p) => {
        if (p.total && p.current !== undefined) setRestoreProgress({ restorePct: (p.current / p.total) * 100 });
        if (p.message) setRestoreProgress({ restoreMessage: p.message });
      });
      const ok = final.state === "SUCCESS";
      setRestoreProgress({ restoreSuccess: ok, restoreFinalMessage: final.message ?? (ok ? "Restore complete." : "Restore failed."), restoreDone: true });
      if (ok && network) await loadFor(network.id);
    } catch (err) {
      const msg = err instanceof ApiError
        ? `API error ${err.status}`
        : err instanceof Error ? err.message : "unknown error";
      setRestoreProgress({ restoreSuccess: false, restoreFinalMessage: msg, restoreDone: true });
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">Operations</Link>
        <span className="opacity-40">/</span>
        <Link to="/meraki/provisioning" className="text-fg-dim hover:text-fg">Meraki</Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Network Backup</span>
      </div>

      <MerakiSubNav />
      <NoVendorKeysBanner vendor="meraki" />

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Network Backup
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Capture a network's full configuration so it can be migrated to a new organization,
            then restored from this snapshot. Backups are encrypted at rest and retained per the
            GuestTek retention policy.
          </p>
        </div>
        <Badge variant="info" size="md">
          <ShieldCheck size={11} />
          Encrypted at rest
        </Badge>
      </div>

      {error ? (
        <output className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach">
          <AlertTriangle size={16} />
          <span><b className="font-semibold">Showing fixture data.</b> <span className="text-peach/80">{error}</span></span>
        </output>
      ) : null}

      <Card>
        <CardContent>
          <Stepper
            active={0}
            steps={[
              { id: "backup", title: "Backup", hint: "Snapshot configuration · this page" },
              { id: "migrate", title: "Migrate", hint: "Move network in Meraki dashboard" },
              { id: "restore", title: "Restore", hint: "Apply snapshot to new network" },
            ]}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>Pick the network to back up</CardTitle>
              <CardSub className="mt-1">We pull the snapshot through your active Meraki key.</CardSub>
            </div>
            <a
              href="https://documentation.meraki.com/General_Administration/Other_Topics/Bulk_Configuration_Updates_via_Action_Batches"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet hover:opacity-80"
            >
              How does migration work?
              <ExternalLink size={11} />
            </a>
          </div>
          <NetworkPicker value={network} onChange={(n) => { setNetwork(n); if (n) void loadFor(n.id); }} />
          <div className="flex items-center justify-between border-t border-line pt-4">
            <div className="flex items-center gap-2 text-xs text-fg-mute">
              <Info size={12} />
              {network ? (
                <span>
                  <b className="font-semibold text-fg">{network.productTypes.length}</b> product
                  {network.productTypes.length === 1 ? "" : "s"} ·{" "}
                  <b className="font-semibold text-fg">{network.deviceCount ?? 0}</b> devices in scope.
                </span>
              ) : (
                <span>Pick a network to see what will be backed up.</span>
              )}
            </div>
            <Button onClick={() => void onRunBackup()} disabled={!network}>
              <ArrowDownToLine size={14} />
              Run backup
            </Button>
          </div>
        </CardContent>
      </Card>

      {network ? (
        <BackupHistorySection
          network={network}
          backups={backups}
          status={status}
          loadFor={loadFor}
          onClickRestore={onClickRestore}
        />
      ) : null}

      <BackupProgressDialog
        backupOpen={backupOpen}
        backupDone={backupDone}
        backupMessage={backupMessage}
        backupPct={backupPct}
        backups={backups}
        setBackup={setBackup}
      />

      <RestoreSelectionDialog
        restoreTarget={restoreTarget}
        restoreNetwork={restoreNetwork}
        restoreUseSame={restoreUseSame}
        resolvedRestoreNetworkId={resolvedRestoreNetworkId}
        setRestoreSel={setRestoreSel}
        onConfirmRestore={() => void onConfirmRestore()}
      />

      <RestoreProgressDialog
        restoreProgressOpen={restoreProgressOpen}
        restoreDone={restoreDone}
        restoreSuccess={restoreSuccess}
        restoreFinalMessage={restoreFinalMessage}
        restoreMessage={restoreMessage}
        restorePct={restorePct}
        setRestoreProgress={setRestoreProgress}
      />
    </div>
  );
}
